import { CANVAS_SNAP_GAP } from '~/design-system/components/DeckGen/utils/canvasSnap'
import { getSlideWorldPositionForSlide } from '~/design-system/components/DeckGen/utils/slideCanvasLayout'
import {
  CANVAS_HEIGHT,
  CANVAS_WIDTH,
  type Presentation,
} from '~/design-system/components/DeckGen/types/presentation'

/** The placement families. A new item cascades next to existing items of its OWN kind: notes beside
 *  notes, docs beside docs, slides beside slides, etc. Free text/shape canvas elements share one kind. */
export type CanvasItemKind = 'slide' | 'document' | 'note' | 'image' | 'webcard' | 'moodboard' | 'element'

/** A world-space box: top-left + size. The unit every canvas item reduces to for placement math. */
export type PlacementRect = { x: number; y: number; width: number; height: number }

/** A placement rect tagged with which family it belongs to, so we can find same-kind neighbours. */
export type TypedRect = PlacementRect & { kind: CanvasItemKind }

/** The on-screen world rect (what's currently visible), as reported by InfiniteCanvas while panning. */
export type WorldRect = { minX: number; minY: number; maxX: number; maxY: number }

/** Two boxes overlap when they intersect on BOTH axes (touching edges don't count as overlap). */
function overlaps(a: PlacementRect, b: PlacementRect): boolean {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y
}

/**
 * Every top-level canvas item as a TYPED world rect: slides, document cards, notes, standalone images,
 * web cards, and free canvas elements (text/shapes). The kind drives same-type cascading; the rects are
 * also the obstacle set a freshly-created item must not overlap.
 */
export function collectCanvasObstacles(presentation: Presentation): TypedRect[] {
  const rects: TypedRect[] = []

  presentation.slides.forEach((slide, index) => {
    const pos = getSlideWorldPositionForSlide(slide, index)
    rects.push({ kind: 'slide', x: pos.x, y: pos.y, width: CANVAS_WIDTH, height: CANVAS_HEIGHT })
  })
  for (const card of presentation.documentCards ?? []) rects.push({ kind: 'document', x: card.x, y: card.y, width: card.width, height: card.height })
  for (const note of presentation.notes ?? []) rects.push({ kind: 'note', x: note.x, y: note.y, width: note.width, height: note.height })
  for (const image of presentation.canvasImages ?? []) rects.push({ kind: 'image', x: image.x, y: image.y, width: image.width, height: image.height })
  for (const card of presentation.webCards ?? []) rects.push({ kind: 'webcard', x: card.x, y: card.y, width: card.width, height: card.height })
  for (const board of presentation.moodboards ?? []) rects.push({ kind: 'moodboard', x: board.x, y: board.y, width: board.width, height: board.height })
  for (const el of presentation.canvasElements) rects.push({ kind: 'element', x: el.x, y: el.y, width: el.width, height: el.height })

  return rects
}

/**
 * Pick a NON-OVERLAPPING world center for a new item of the given size, given a DESIRED center (usually
 * a same-type anchor for a toolbar add, or the cursor for a right-click add).
 *
 * If the desired spot is already clear, it's returned untouched. Otherwise the item is pushed to the
 * RIGHT of whatever it overlaps, leaving the canvas snap gap (64px) — the same spacing it would settle
 * into if you dragged it beside a neighbour and let snapping engage. We keep stepping right past each
 * obstacle until the box is clear, so a row of existing items just extends one slot further right.
 */
export function findCanvasPlacement(
  desiredCenter: { x: number; y: number },
  size: { width: number; height: number },
  obstacles: PlacementRect[],
): { x: number; y: number } {
  // Work in top-left space (every item's stored x/y is its top-left), then convert back to a center.
  let left = Math.round(desiredCenter.x - size.width / 2)
  const top = Math.round(desiredCenter.y - size.height / 2)

  // Probe rightward: on each collision, jump to the right edge of the blocking obstacle + the snap gap,
  // then re-check against all obstacles (the new slot may now overlap a different one). Bounded by the
  // obstacle count so it always terminates — each step clears at least the obstacle it jumped past.
  for (let guard = 0; guard <= obstacles.length; guard += 1) {
    const box: PlacementRect = { x: left, y: top, width: size.width, height: size.height }
    const blocker = obstacles.find((o) => overlaps(box, o))
    if (!blocker) break
    left = Math.round(blocker.x + blocker.width + CANVAS_SNAP_GAP)
  }

  return { x: left + size.width / 2, y: top + size.height / 2 }
}

/**
 * Find the NEAREST world center where an item of `size` overlaps no obstacle, searching OUTWARD from
 * `desiredCenter` in every direction (not just rightward like {@link findCanvasPlacement}). If the anchor
 * is already clear it's returned untouched; otherwise we probe expanding rings — nearest radius first —
 * and return the closest clear candidate found. This is the "drop it in the nearest empty gap" placement
 * used by paste / duplicate / toolbar / drop, so a new item never lands on top of an existing one and
 * stays as close as possible to where it was asked for. Falls back to {@link findCanvasPlacement} (the
 * always-terminating rightward cascade) only if the ring search is exhausted on a pathologically dense canvas.
 */
export function findNearestEmptySpace(
  desiredCenter: { x: number; y: number },
  size: { width: number; height: number },
  obstacles: PlacementRect[],
  gap: number = CANVAS_SNAP_GAP,
): { x: number; y: number } {
  const isClear = (cx: number, cy: number): boolean => {
    const box: PlacementRect = {
      x: cx - size.width / 2,
      y: cy - size.height / 2,
      width: size.width,
      height: size.height,
    }
    return !obstacles.some((o) => overlaps(box, o))
  }

  if (isClear(desiredCenter.x, desiredCenter.y)) {
    return { x: Math.round(desiredCenter.x), y: Math.round(desiredCenter.y) }
  }

  // One ring out is big enough to clear a same-size obstacle plus the gap, so the search converges fast
  // even for slide-sized items without sampling uselessly fine for tiny ones.
  const minSide = Math.max(1, Math.min(size.width, size.height))
  const step = Math.max(gap, minSide / 2 + gap)
  const MAX_RINGS = 80

  for (let ring = 1; ring <= MAX_RINGS; ring += 1) {
    const radius = ring * step
    // Sample densely enough that the angular spacing between probes is under half the item's smaller side,
    // so the search can't "tunnel" past a real gap that sits between two samples. Bigger rings → more samples.
    const samples = Math.max(8, Math.ceil((2 * Math.PI * radius) / (minSide / 2)))
    let best: { x: number; y: number } | null = null
    let bestDist = Infinity
    for (let s = 0; s < samples; s += 1) {
      const angle = (s / samples) * Math.PI * 2
      const cx = desiredCenter.x + Math.cos(angle) * radius
      const cy = desiredCenter.y + Math.sin(angle) * radius
      if (isClear(cx, cy)) {
        const dist = (cx - desiredCenter.x) ** 2 + (cy - desiredCenter.y) ** 2
        if (dist < bestDist) {
          bestDist = dist
          best = { x: cx, y: cy }
        }
      }
    }
    // First ring with any clear candidate holds the nearest slot (rings grow monotonically) → return it.
    if (best) return { x: Math.round(best.x), y: Math.round(best.y) }
  }

  return findCanvasPlacement(desiredCenter, size, obstacles)
}

/** The tight world bounding box (top-left + size) around a set of rects. Empty set → a zero box at origin. */
export function unionBounds(rects: PlacementRect[]): PlacementRect {
  if (rects.length === 0) return { x: 0, y: 0, width: 0, height: 0 }
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const r of rects) {
    if (r.x < minX) minX = r.x
    if (r.y < minY) minY = r.y
    if (r.x + r.width > maxX) maxX = r.x + r.width
    if (r.y + r.height > maxY) maxY = r.y + r.height
  }
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY }
}

/**
 * The translation that moves a GROUP of items (notes/docs/slides copied together) so their bounding box
 * is centered on `anchorCenter` AND clears every obstacle — while KEEPING their relative arrangement.
 * Used by multi-item paste / duplicate: union the item rects, find the nearest empty slot for a
 * bbox-sized rect at the anchor, and return the `{dx,dy}` the caller adds to every item's x/y. The cluster
 * keeps its shape; the whole block lands in a clear gap. `obstacles` should be the EXISTING items only
 * (not the copies themselves, which move together and so never self-collide).
 */
export function nearestEmptyGroupTranslation(
  itemRects: PlacementRect[],
  anchorCenter: { x: number; y: number },
  obstacles: PlacementRect[],
  gap: number = CANVAS_SNAP_GAP,
): { dx: number; dy: number } {
  if (itemRects.length === 0) return { dx: 0, dy: 0 }
  const bbox = unionBounds(itemRects)
  const bboxCenter = { x: bbox.x + bbox.width / 2, y: bbox.y + bbox.height / 2 }
  const target = findNearestEmptySpace(anchorCenter, { width: bbox.width, height: bbox.height }, obstacles, gap)
  return { dx: Math.round(target.x - bboxCenter.x), dy: Math.round(target.y - bboxCenter.y) }
}
